import { Component, OnInit, Input } from '@angular/core';
import { DataSharedService } from '../services/data-shared.service';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { NgbModal, NgbActiveModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { auditTrailHeader, SuccessModal } from '../app.model';
import { SuccessModalComponent } from '../components/success-modal/success-modal.component';
import { InvoicesService } from '../services/invoices.service';
import { AuthenticationService } from '../services/authentication.service';
import { roleConstant } from '../app.constant';
import { Router } from '@angular/router';
import { DatePipe } from '@angular/common';



@Component({
  selector: 'app-invoice-details',
  templateUrl: './invoice-details.component.html',
  styleUrls: ['./invoice-details.component.css']
})
export class InvoiceDetailsComponent implements OnInit {

  @Input() public invoice: any;
  @Input() public invoiceType: string;
  invoiceDetails: FormGroup | any;
  candidateTrackingHrs: any = localStorage.getItem('candidateTrackingHrs') ? localStorage.getItem('candidateTrackingHrs') : 1;
  editClientInvoiceDate = false;
  editContractInvoiceDate = false;
  editClientDueDate = false;
  editContractDueDate = false;
  clientDueDateEdited = false;
  contractDueDateEdited = false;
  editInvoiceNumber = false;
  editClientInvoiceNumber = false;
  editPoNumber = false;
  selectedInvoiceDate: Date;
  ROLES = roleConstant;
  user: any;
  tab: 'details' | 'history' = 'details';
  auditHeader!: auditTrailHeader;


  constructor(
    private activeModalService: NgbActiveModal,
    public dataSharedService: DataSharedService,
    private modalService: NgbModal,
    public invoiceService: InvoicesService,
    private authService: AuthenticationService,
    public router: Router,
    public datePipe: DatePipe,
  ) { }

  ngOnInit(): void {
    this.user = this.authService.userValue;
    this.invoiceDetails = new FormGroup({
      client_invoice_date: new FormControl(this.invoice.client_invoice_date, Validators.required),
      invoice_date: new FormControl(this.invoice.invoice_date, Validators.required),
      client_due_date: new FormControl(this.invoice.client_due_date, Validators.required),
      due_date: new FormControl(this.invoice.due_date, Validators.required),
      invoice_number: new FormControl(this.invoice.invoice_number),
      client_invoice_number: new FormControl(this.invoice.client_invoice_number),
      po_number: new FormControl(this.invoice.po_number),
    });
    this.auditHeader = <auditTrailHeader>{
      title: 'Audit Trail',
      subTitle: this.invoiceType === 'client'
        ? this.invoice.client_invoice_number
        : this.invoice.invoice_number,
      icon: 'bi bi-clock-history',
    };
  }

  get isAgencyAdmin(): boolean {
    return this.user?.role_uuid === this.ROLES.AGENCY;
  }

  get isSent(): boolean {
    return this.invoiceType === 'client'
      ? this.invoice?.client_sent_display === 'Sent'
      : this.invoice?.contractor_sent_display === 'Sent';
  }

  // Fields can only be edited by an agency admin while the invoice has not been sent.
  get canEdit(): boolean {
    return this.isAgencyAdmin && !this.isSent;
  }

  onClose() {
    this.activeModalService.close();
  }

  onUpdate(invoiceType: any) {

    if (!this.invoiceDetails.valid) return;

    const formValue = this.invoiceDetails.value;
    const payload: any = invoiceType === "client"
      ? {
        client_invoice_date: this.formatDate(formValue.client_invoice_date),
        client_invoice_number: formValue.client_invoice_number,
      }
      : {
        invoice_date: this.formatDate(formValue.invoice_date),
        invoice_number: formValue.invoice_number,
      };

    // PO number is shared across both invoice types.
    payload.po_number = formValue.po_number;

    // Only send the due date when the user explicitly edited it, so an
    // untouched due date still recalculates from the invoice date on the backend.
    if (invoiceType === "client" && this.clientDueDateEdited) {
      payload.client_due_date = this.formatDate(formValue.client_due_date);
    }
    if (invoiceType !== "client" && this.contractDueDateEdited) {
      payload.due_date = this.formatDate(formValue.due_date);
    }
    this.invoiceService.updateInvoice(this.invoice.uuid, payload).subscribe({
      next: () => {
        this.successModal('Invoice updated successfully.');
        this.activeModalService.close();
        this.reloadComponent(false, invoiceType === "client"
          ? 'invoices/bill-invoices'
          : 'invoices/pay-invoices');
      },
      error: (err) => {
        console.error(err);
      }
    });
  }
  onClientInvoiceDateChanged(event: any) {
    this.invoice.client_invoice_date = event.value;
    this.invoiceDetails.get('client_invoice_date')?.setValue(event.value);
    this.editClientInvoiceDate = false;
  }

  onContractorInvoiceDateChanged(event: any) {
    this.invoice.invoice_date = event.value;
    this.invoiceDetails.get('invoice_date')?.setValue(event.value);
    this.editContractInvoiceDate = false;
  }

  onClientDueDateChanged(event: any) {
    this.invoice.client_due_date = event.value;
    this.invoiceDetails.get('client_due_date')?.setValue(event.value);
    this.clientDueDateEdited = true;
    this.editClientDueDate = false;
  }

  onContractorDueDateChanged(event: any) {
    this.invoice.due_date = event.value;
    this.invoiceDetails.get('due_date')?.setValue(event.value);
    this.contractDueDateEdited = true;
    this.editContractDueDate = false;
  }

  onSend(invoiceType: any, invoiceId: any) {

    const payload = {
      'invoice_type': invoiceType,
    }
    this.invoiceService.sendInvoices([invoiceId], payload).subscribe({
      next: (response) => {
        this.successModal('Invoice Sent Successfully.');
        this.activeModalService.close();
        this.reloadComponent(false, invoiceType == "client"
          ? 'invoices/bill-invoices'
          : 'invoices/pay-invoices');
      },
      error: (err) => {
      }
    });
  }

  reloadComponent(self: boolean, urlToNavigateTo?: string) {
    //skipLocationChange:true means dont update the url to / when navigating
    console.log("Current route I am on:", this.router.url);
    const url = self ? this.router.url : urlToNavigateTo;
    this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
      this.router.navigate([`/${url}`]).then(() => {
        console.log(`After navigation I am on:${this.router.url}`)
      })
    })
  }

  successModal(message: string) {
    const modalRef = this.modalService.open(SuccessModalComponent, {
      size: 'sm',
      modalDialogClass: 'success-modal',
      backdrop: false,
    });
    let data: SuccessModal = {
      message: message,
      image: 'assets/img/checkmark.png',
      duration: 2000,
    }
    modalRef.componentInstance.data = data;
    modalRef.result.then((data) => {
      if (data?.action === 'yes') {
        this.activeModalService.close();
      }
    });
  }

  formatDate = (date: Date): any => {
    return this.datePipe.transform(date, 'yyyy-MM-dd')!;

  };

}
